import { useState } from "react";
import { Box, Badge, Button, Input } from "@chakra-ui/react";
import { FiDownload, FiChevronLeft, FiMoreVertical } from "react-icons/fi";
import { CiFilter } from "react-icons/ci";
import { GoSortAsc } from "react-icons/go";
import { AiOutlineSearch } from "react-icons/ai";

interface PharmacyInfo {
  name: string;
  registrationNo: string;
  type: string;
  location: string;
  operatingHours: string;
  region: string;
  phone: string;
  website: string;
  ownerName: string;
  email: string;
  status: "Active" | "Inactive";
  license: "Valid License" | "Expired";
}

interface InventoryItem {
  id: number;
  sku: string;
  productName: string;
  category: string;
  stockLevel: number;
  status: "In Stock" | "Low Stock" | "Out of Stock";
  unitSold?: number;
}

const pharmacyInfo: PharmacyInfo = {
  name: "Naza Pharmacy",
  registrationNo: "1234567",
  type: "Wholesale & Retail",
  location: "Kogo Phase 1, Bwari Abuja",
  operatingHours: "Mon-Sun: 9 AM - 8 PM",
  region: "North Central",
  phone: "+2348000000",
  website: "www.nazapharmacy.com",
  ownerName: "John Doe",
  email: "contact@nazapharmacy.com",
  status: "Active",
  license: "Valid License",
};

const inventoryData: InventoryItem[] = [
  {
    id: 1,
    sku: "12345",
    productName: "Arthemeter",
    category: "Anti Malaria",
    stockLevel: 500,
    status: "In Stock",
    unitSold: 500,
  },
  {
    id: 2,
    sku: "12346",
    productName: "Chloroquine",
    category: "Anti Malaria",
    stockLevel: 300,
    status: "In Stock",
    unitSold: 300,
  },
  {
    id: 3,
    sku: "12347",
    productName: "Doxycycline",
    category: "Anti Malaria",
    stockLevel: 50,
    status: "Low Stock",
    unitSold: 50,
  },
  {
    id: 4,
    sku: "12348",
    productName: "Quinine",
    category: "Anti Malaria",
    stockLevel: 450,
    status: "In Stock",
    unitSold: 450,
  },
  {
    id: 5,
    sku: "12349",
    productName: "Mefloquine",
    category: "Anti Malaria",
    stockLevel: 600,
    status: "In Stock",
    unitSold: 600,
  },
  {
    id: 6,
    sku: "12350",
    productName: "Primaquine",
    category: "Anti Malaria",
    stockLevel: 200,
    status: "In Stock",
    unitSold: 200,
  },
  {
    id: 7,
    sku: "12351",
    productName: "Artemether-Lumefantrine",
    category: "Anti Malaria",
    stockLevel: 700,
    status: "In Stock",
    unitSold: 700,
  },
  {
    id: 8,
    sku: "12352",
    productName: "Atovaquone-Proguanil",
    category: "Anti Malaria",
    stockLevel: 400,
    status: "In Stock",
    unitSold: 400,
  },
  {
    id: 9,
    sku: "12353",
    productName: "Sulphadoxine-Pyrimethamine",
    category: "Anti Malaria",
    stockLevel: 0,
    status: "Out of Stock",
    unitSold: 0,
  },
  {
    id: 10,
    sku: "12354",
    productName: "Lariam",
    category: "Anti Malaria",
    stockLevel: 350,
    status: "In Stock",
    unitSold: 350,
  },
];

const salesData: InventoryItem[] = [
  {
    id: 1,
    sku: "12345",
    productName: "Arthemeter",
    category: "Anti Malaria",
    stockLevel: 500,
    status: "In Stock",
    unitSold: 450,
  },
  {
    id: 2,
    sku: "12346",
    productName: "Chloroquine",
    category: "Anti Malaria",
    stockLevel: 300,
    status: "In Stock",
    unitSold: 280,
  },
];

const complianceData: InventoryItem[] = [
  {
    id: 1,
    sku: "12345",
    productName: "Arthemeter",
    category: "Anti Malaria",
    stockLevel: 500,
    status: "In Stock",
  },
  {
    id: 2,
    sku: "12346",
    productName: "Chloroquine",
    category: "Anti Malaria",
    stockLevel: 300,
    status: "In Stock",
  },
];

const PharmacyCard = ({ info }: { info: PharmacyInfo }) => (
  <Box bg="white" p={6} borderRadius="xl" shadow="sm" borderWidth="1px">
    <Box display="flex" alignItems="start" gap={4}>
      <Box
        w="80px"
        h="80px"
        bg="orange.400"
        borderRadius="xl"
        display="flex"
        alignItems="center"
        justifyContent="center"
        fontSize="2xl"
        fontWeight="bold"
        color="white">
        NAZA
      </Box>

      <Box flex="1">
        <Box display="flex" alignItems="center" gap={2} mb={3}>
          <Box fontSize="sm" color="gray.600" fontWeight="500">
            Account Status
          </Box>
        </Box>
        <Box display="flex" gap={2} mb={4}>
          <Badge
            colorScheme="green"
            px={3}
            py={1}
            borderRadius="md"
            fontSize="sm">
            {info.license}
          </Badge>
          <Badge
            colorScheme="blue"
            px={3}
            py={1}
            borderRadius="md"
            fontSize="sm">
            {info.status}
          </Badge>
        </Box>

        <Box
          display="grid"
          gridTemplateColumns="repeat(2, 1fr)"
          gap={4}
          fontSize="sm">
          <Box>
            <Box color="gray.600" mb={1}>
              Pharmacy Name:
            </Box>
            <Box fontWeight="600" color="gray.800">
              {info.name}
            </Box>
          </Box>
          <Box>
            <Box color="gray.600" mb={1}>
              Registration No.
            </Box>
            <Box fontWeight="600" color="gray.800">
              {info.registrationNo}
            </Box>
          </Box>

          <Box>
            <Box color="gray.600" mb={1}>
              Pharmacy type Type
            </Box>
            <Box fontWeight="600" color="gray.800">
              {info.type}
            </Box>
          </Box>
          <Box>
            <Box color="gray.600" mb={1}>
              Location:
            </Box>
            <Box fontWeight="600" color="gray.800">
              {info.location}
            </Box>
          </Box>

          <Box>
            <Box color="gray.600" mb={1}>
              Operating Hours
            </Box>
            <Box fontWeight="600" color="gray.800">
              {info.operatingHours}
            </Box>
          </Box>
          <Box>
            <Box color="gray.600" mb={1}>
              Region
            </Box>
            <Box fontWeight="600" color="gray.800">
              {info.region}
            </Box>
          </Box>

          <Box>
            <Box color="gray.600" mb={1}>
              Phone
            </Box>
            <Box fontWeight="600" color="gray.800">
              {info.phone}
            </Box>
          </Box>
          <Box>
            <Box color="gray.600" mb={1}>
              Website
            </Box>
            <Box fontWeight="600" color="gray.800">
              {info.website}
            </Box>
          </Box>

          <Box>
            <Box color="gray.600" mb={1}>
              Owner Name:
            </Box>
            <Box fontWeight="600" color="gray.800">
              {info.ownerName}
            </Box>
          </Box>
          <Box>
            <Box color="gray.600" mb={1}>
              Email Address
            </Box>
            <Box fontWeight="600" color="gray.800">
              {info.email}
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  </Box>
);

const InventoryTable = ({ data }: { data: InventoryItem[] }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case "In Stock":
        return "green";
      case "Low Stock":
        return "yellow";
      case "Out of Stock":
        return "red";
      default:
        return "gray";
    }
  };

  return (
    <Box overflowX="auto">
      <table
        style={{ width: "100%", borderCollapse: "separate", borderSpacing: 0 }}>
        <thead>
          <tr style={{ backgroundColor: "#F9FAFB" }}>
            <th
              style={{
                padding: "16px",
                textAlign: "left",
                fontSize: "14px",
                fontWeight: "600",
                color: "#374151",
              }}>
              <input
                type="checkbox"
                style={{ width: "16px", height: "16px" }}
              />
            </th>
            <th
              style={{
                padding: "16px",
                textAlign: "left",
                fontSize: "14px",
                fontWeight: "600",
                color: "#374151",
              }}>
              SKUs
            </th>
            <th
              style={{
                padding: "16px",
                textAlign: "left",
                fontSize: "14px",
                fontWeight: "600",
                color: "#374151",
              }}>
              Product Name
            </th>
            <th
              style={{
                padding: "16px",
                textAlign: "left",
                fontSize: "14px",
                fontWeight: "600",
                color: "#374151",
              }}>
              Category
            </th>
            <th
              style={{
                padding: "16px",
                textAlign: "left",
                fontSize: "14px",
                fontWeight: "600",
                color: "#374151",
              }}>
              Stock Level
            </th>
            <th
              style={{
                padding: "16px",
                textAlign: "left",
                fontSize: "14px",
                fontWeight: "600",
                color: "#374151",
              }}>
              Unit Sold
            </th>
            <th
              style={{
                padding: "16px",
                textAlign: "left",
                fontSize: "14px",
                fontWeight: "600",
                color: "#374151",
              }}></th>
          </tr>
        </thead>
        <tbody>
          {data.map((item) => (
            <tr key={item.id} style={{ borderBottom: "1px solid #E5E7EB" }}>
              <td style={{ padding: "16px" }}>
                <input
                  type="checkbox"
                  style={{ width: "16px", height: "16px" }}
                />
              </td>
              <td
                style={{
                  padding: "16px",
                  color: "#374151",
                  fontWeight: "500",
                }}>
                {item.sku}
              </td>
              <td
                style={{
                  padding: "16px",
                  color: "#374151",
                  fontWeight: "500",
                }}>
                {item.productName}
              </td>
              <td style={{ padding: "16px", color: "#6B7280" }}>
                {item.category}
              </td>
              <td style={{ padding: "16px" }}>
                <Badge
                  colorScheme={getStatusColor(item.status)}
                  px={3}
                  py={1}
                  borderRadius="md"
                  fontSize="sm">
                  {item.status}
                </Badge>
              </td>
              <td
                style={{
                  padding: "16px",
                  color: "#374151",
                  fontWeight: "600",
                }}>
                {item.stockLevel}
              </td>
              <td style={{ padding: "16px", textAlign: "center" }}>
                <button
                  style={{
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                  }}>
                  <FiMoreVertical size={20} />
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        p={4}
        borderTopWidth="1px"
        bg="white">
        <Box color="gray.600" fontSize="sm">
          Showing 10 Of 10,000
        </Box>
        <Box display="flex" gap={2}>
          <Button size="sm" variant="ghost">
            &lt;
          </Button>
          <Button size="sm" colorScheme="blue">
            1
          </Button>
          <Button size="sm" variant="ghost">
            2
          </Button>
          <Button size="sm" variant="ghost">
            3
          </Button>
          <Button size="sm" variant="ghost">
            &gt;
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default function PharmacyDetail() {
  const [activeTab, setActiveTab] = useState(0);

  const tabs = [
    { label: "Inventory", data: inventoryData },
    { label: "Sales", data: salesData },
    { label: "Compliance tracking", data: complianceData },
  ];

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={6}>
        <Box display="flex" alignItems="center" gap={3}>
          <Button variant="ghost" size="sm" p={0}>
            <FiChevronLeft size={24} />
          </Button>
          <Box fontSize="3xl" fontWeight="bold">
            Naza Pharmacy
          </Box>
        </Box>

        <Box display="flex" gap={3}>
          <Button colorScheme="blue" size="md">
            <FiDownload /> Download Report
          </Button>
          <Button variant="outline" size="md">
            Manage Pharmacy
          </Button>
        </Box>
      </Box>

      <PharmacyCard info={pharmacyInfo} />

      <Box bg="white" mt={6} borderRadius="xl" shadow="sm" borderWidth="1px">
        {/* Tabs Header */}
        <Box borderBottomWidth="1px">
          <Box display="flex" px={6}>
            {tabs.map((tab, index) => (
              <Box
                key={index}
                px={4}
                py={4}
                cursor="pointer"
                borderBottomWidth="2px"
                borderBottomColor={
                  activeTab === index ? "blue.600" : "transparent"
                }
                color={activeTab === index ? "blue.600" : "gray.600"}
                fontWeight={activeTab === index ? "600" : "500"}
                onClick={() => setActiveTab(index)}
                _hover={{ color: "blue.600" }}>
                {tab.label}
              </Box>
            ))}
          </Box>
        </Box>

        {/* Search and Filter Bar */}
        <Box p={6} display="flex" gap={3} alignItems="center">
          <Box position="relative" flex="1" maxW="400px">
            <Box
              position="absolute"
              left="12px"
              top="50%"
              transform="translateY(-50%)">
              <AiOutlineSearch size={20} color="#9CA3AF" />
            </Box>
            <Input
              placeholder="Search"
              pl="40px"
              bg="white"
              borderColor="gray.300"
            />
          </Box>
          <Button variant="outline">
            <CiFilter /> Filter
          </Button>
          <Button variant="outline">
            <GoSortAsc /> Sort By
          </Button>
        </Box>

        {/* Table */}
        <InventoryTable data={tabs[activeTab].data} />
      </Box>
    </Box>
  );
}
